import { ref } from 'vue'
import type { UserInfoResponse } from '@logto/vue'
import { logger } from '@/utils/logger'
import { logtoStorage } from '@/utils/storage/logto'
import { usePostHog } from '@/shared/composables/usePostHog'

const { posthog } = usePostHog()

export type AuthBroadcastMessage = {
  at: number
  reason: string
  sourceTabId: string
  type: 'snapshot' | 'logout'
}

type CreateUserSyncOptions = {
  authBroadcastChannel: string
  authBroadcastKey: string
  refreshLockKey: string
  extensionHelloResponseCooldownMs: number
  isExtensionDebugEnabled: boolean
  tabId: string
  getToken: () => string | null
  getUser: () => UserInfoResponse | null
  onStorageSync: (reason: string) => void
  onBroadcastSync: (reason: string) => void
  onExtensionFirstSeen: (eventType?: string) => void
}

export const createUserSync = (options: CreateUserSyncOptions) => {
  const extensionLastSeenAt = ref<number | null>(null)
  let extensionLastHelloResponseAt = 0
  let syncListenersBound = false

  const authChannel =
    typeof window !== 'undefined' && 'BroadcastChannel' in window
      ? new BroadcastChannel(options.authBroadcastChannel)
      : null

  const sendTokenToExtension = (token: string | null, userInfo: UserInfoResponse | null = null) => {
    if (typeof window === 'undefined') return

    if (options.isExtensionDebugEnabled) {
      logger.debug('🔌 [EasyHire] Sending token & user info to extension:', {
        hasToken: !!token,
        hasUser: !!userInfo,
      })
    }

    const userInfoPlain = userInfo ? JSON.parse(JSON.stringify(userInfo)) : null
    const refreshToken = token ? logtoStorage.getRefreshToken() : null

    window.postMessage(
      {
        type: 'EASYHIRE_EXTENSION_SYNC',
        token,
        refreshToken,
        userInfo: userInfoPlain,
      },
      window.location.origin,
    )
  }

  const markExtensionSeen = (eventType?: string) => {
    const isFirstSeen = extensionLastSeenAt.value === null
    extensionLastSeenAt.value = Date.now()
    if (isFirstSeen) {
      options.onExtensionFirstSeen(eventType)
    }
  }

  const emitAuthEvent = (type: AuthBroadcastMessage['type'], reason: string) => {
    if (typeof window === 'undefined') return
    const payload: AuthBroadcastMessage = {
      at: Date.now(),
      reason,
      sourceTabId: options.tabId,
      type,
    }
    authChannel?.postMessage(payload)
    try {
      window.localStorage.setItem(options.authBroadcastKey, JSON.stringify(payload))
    } catch (error) {
      console.error('[UserSync] 写入认证广播消息失败:', error)
      posthog.capture('storage_error', {
        operation: 'write_auth_broadcast',
        storageType: 'localStorage',
        error: String(error),
      })
      // Ignore storage write failures.
    }
  }

  const handleExtensionMessage = (event: MessageEvent) => {
    // This is a same-window bridge, not an authentication boundary against page scripts.
    if (event.source !== window || event.origin !== window.location.origin) return
    if (!event.data || typeof event.data !== 'object' || Array.isArray(event.data)) return

    const eventType = (event.data as { type?: unknown }).type
    if (typeof eventType !== 'string') return
    const isSyncRequest =
      eventType === 'EASYHIRE_EXTENSION_REQUEST_SYNC' ||
      eventType === 'EASYHIRE_EXTENSION_CHECK_AUTH'
    const isHello = eventType === 'EASYHIRE_EXTENSION_HELLO'

    if (!isSyncRequest && !isHello) return

    markExtensionSeen(eventType)
    if (options.isExtensionDebugEnabled) {
      logger.debug('📩 [EasyHire] Received sync request from extension:', eventType)
    }

    if (isHello) {
      const now = Date.now()
      if (now - extensionLastHelloResponseAt < options.extensionHelloResponseCooldownMs) {
        return
      }
      extensionLastHelloResponseAt = now
    }

    sendTokenToExtension(options.getToken(), options.getUser())
  }

  const handleStorageEvent = (event: StorageEvent) => {
    if (!event.key) return
    if (
      event.key === options.authBroadcastKey ||
      event.key === options.refreshLockKey ||
      event.key === 'token' ||
      event.key === 'user' ||
      event.key === 'easyhire_admin_mode'
    ) {
      options.onStorageSync(`storage:${event.key}`)
    }
  }

  const handleAuthChannelMessage = (event: MessageEvent<AuthBroadcastMessage>) => {
    const message = event.data as AuthBroadcastMessage | undefined
    if (!message || message.sourceTabId === options.tabId) return
    options.onBroadcastSync(`broadcast:${message.reason}`)
  }

  const detachSyncListeners = () => {
    if (typeof window === 'undefined' || !syncListenersBound) return
    window.removeEventListener('message', handleExtensionMessage)
    window.removeEventListener('storage', handleStorageEvent)
    authChannel?.removeEventListener('message', handleAuthChannelMessage)
    syncListenersBound = false
  }

  const attachSyncListeners = () => {
    if (typeof window === 'undefined') return
    if (syncListenersBound) {
      detachSyncListeners()
    }
    window.addEventListener('message', handleExtensionMessage)
    window.addEventListener('storage', handleStorageEvent)
    authChannel?.addEventListener('message', handleAuthChannelMessage)
    syncListenersBound = true
  }

  const close = () => {
    detachSyncListeners()
    authChannel?.close()
  }

  return {
    extensionLastSeenAt,
    sendTokenToExtension,
    markExtensionSeen,
    emitAuthEvent,
    attachSyncListeners,
    detachSyncListeners,
    close,
  }
}
